import {
  KeyboardEventHandler,
  MouseEventHandler,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef
} from "react"

import classNames from "classnames"

import { ENTER_KEY } from "shared/lib/keys"
import { noop } from "shared/lib/noop"
import { useEvent } from "shared/lib/useEvent"
import { useMergedRefs } from "shared/lib/useMergedRefs"
import { Ref } from "shared/types/Ref"
import { WithID } from "shared/types/WithID"

import { useDraggable } from "../../../lib/drag-and-drop/useDraggable"
import { useDroppable } from "../../../lib/drag-and-drop/useDroppable"
import { TableContext } from "../TableContext"
import { TableItemProps, TableItemRendererProps } from "../types"
import { TableDragMeta } from "../types/TableDragMeta"

export function useTableItem<I extends WithID>(
  props: TableItemProps<I>
): TableItemRendererProps {
  const id = useId()
  const ref = useRef<HTMLDivElement>(null)

  const { item, insertBefore, insertAfter, index, parentID } = props
  const {
    isDragDisabled,
    canDrag,
    isTableFocused,
    focusedRow,
    focusedCell,
    isItemCollapsed,
    availableDragTypes,
    getDragType,
    getRowCells,
    getDepth,
    focusTable,
    onRowFocus,
    isItemSelected,
    onSelect,
    setFocusableElement
  } = useContext(TableContext)

  const isSelected = !!isItemSelected?.(item)

  const handleSelect = useEvent(
    (ev: React.MouseEvent | React.KeyboardEvent) => {
      onSelect?.(item.id, index, {
        isMulti: ev.ctrlKey || ev.metaKey,
        isRange: ev.shiftKey
      })
      onRowFocus?.(index)
    }
  )

  const handleMouseSelect: MouseEventHandler = useEvent(ev => {
    handleSelect(ev)
  })

  const handleKeyboardSelect: KeyboardEventHandler = useEvent(ev => {
    if (ev.key === ENTER_KEY.key) {
      handleSelect(ev)
    }
  })

  const isFocusable = focusedRow === index

  const onUnmount = useEvent(() => {
    if (isTableFocused && isFocusable) {
      focusTable()
    }
  })

  useEffect(() => () => onUnmount(), [])

  const handleFocusCapture = useEvent(() => {
    onRowFocus?.(index)
  })

  const depth = useMemo(() => getDepth?.(item) ?? 1, [item])

  const {
    isDragging,
    attributes,
    listeners,
    setNodeRef: setDraggableRef
  } = useDraggable<typeof item, TableDragMeta>({
    id: `table-drag-${id}-${item.id}`,
    dragSourceID: item.id,
    data: item,
    meta: { index, level: item.level, parentID, depth },
    type: getDragType?.(item) ?? "row",
    disabled: isDragDisabled || (canDrag ? !canDrag(item) : false)
  })

  const { setNodeRef: setDroppableRef } = useDroppable<
    typeof item,
    TableDragMeta
  >({
    id: `table-drop-${id}-${item.id}`,
    data: item,
    meta: { index, level: item.level, parentID, depth },
    type: getDragType?.(item) ?? "row",
    disabled: isDragDisabled,
    accept: availableDragTypes
  })

  const finalRef = useMergedRefs(
    setDraggableRef,
    setDroppableRef,
    ref,
    isFocusable && !focusedCell ? setFocusableElement : undefined
  )

  const rowElementProps: Omit<React.HTMLProps<HTMLDivElement>, "ref"> & {
    ref: Ref<HTMLDivElement>
  } = useMemo(
    () => ({
      ...attributes,
      ...listeners,
      id: `row-${item.id}`,
      role: "row",
      "data-row-id": item.id,
      "aria-expanded":
        !!item.childIDs && item.childIDs.length > 0 && !isItemCollapsed?.(item),
      "aria-rowindex": index + 1,
      "aria-selected": isSelected,
      "aria-level": item.level + 1,
      "aria-posinset": item.position + 1,
      "aria-setsize": item.siblingCount,
      tabIndex: isFocusable && !focusedCell ? 0 : -1,
      ref: finalRef,
      onFocusCapture: handleFocusCapture,
      onClick: handleMouseSelect,
      onKeyDown: handleKeyboardSelect,
      className: classNames({
        "insert-before": insertBefore,
        "insert-after": insertAfter,
        dragging: isDragging
      }),
      style: { ...props.style }
    }),
    [
      focusedCell,
      isFocusable,
      insertBefore,
      insertAfter,
      index,
      item,
      isSelected,
      props.style,
      isDragging,
      finalRef,
      isItemCollapsed,
      attributes,
      listeners
    ]
  )

  const cells = useMemo(() => {
    return getRowCells?.(item) ?? []
  }, [item, getRowCells])

  const getCellProps: (
    key: string
  ) => Omit<React.HTMLProps<HTMLDivElement>, "ref"> = useCallback(
    (key: string) => {
      const cell = cells.find(c => c.key === key)
      const isFocused = cell?.key === focusedCell?.key

      return {
        role: "gridcell",
        "aria-colindex": cell?.colIndex,
        "aria-colspan": cell?.colSpan,
        tabIndex: isFocusable && isFocused ? 0 : -1,
        ref: isFocusable && isFocused ? setFocusableElement : undefined
      }
    },
    [isFocusable, cells, focusedCell]
  )

  return {
    isSelected,
    isDragDisabled: isDragDisabled || isDragging,
    rowElementProps,
    getCellProps,
    isFocused: index === focusedRow && isTableFocused,
    isFocusable: index === focusedRow,
    focusedCell,
    index,
    level: item.level,
    parentIndices: item.parentIndices,
    onRowFocus: noop,
    onSelect: handleSelect
  }
}
